// Supabase client. Configured from REACT_APP_SUPABASE_URL +
// REACT_APP_SUPABASE_ANON_KEY (injected by Vite). When unset (e.g. pure local
// dev against native Postgres), `supabase` is null and the app falls back to the
// dev-token sign-in path. The access_token from a Supabase session is the same
// JWT the FastAPI backend verifies.
import { createClient } from '@supabase/supabase-js';

const url = process.env.REACT_APP_SUPABASE_URL;
const anon = process.env.REACT_APP_SUPABASE_ANON_KEY;

// #157: a magic-link (or OAuth) redirect that FAILS — an expired/already-used
// link, a denied OAuth consent — comes back from Supabase as `#error=...&
// error_description=...` in the URL fragment, not a session. Read + strip it
// HERE, synchronously, before `createClient()` below kicks off supabase-js's own
// async `detectSessionInUrl` parsing of that same fragment (which also clears it,
// but on its own promise-scheduled timeline) — reading first avoids racing it and
// guarantees we see the error exactly once per redirect. Exported standalone
// (rather than folded into a hook) so it's unit-testable without constructing a
// real Supabase client.
export function captureAuthRedirectError() {
  if (typeof window === 'undefined' || !window.location.hash) return null;
  // location.hash is '' or a string starting with '#' per the DOM spec — never
  // anything else — so a plain slice is safe here.
  const hash = window.location.hash.slice(1);
  if (!hash.includes('error=')) return null;
  const params = new URLSearchParams(hash);
  const desc = params.get('error_description');
  // Drop the fragment so a reload/share of the URL doesn't repeat the message and
  // so it can't be misread as belonging to a later navigation on the same page.
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  // Supabase's error codes (access_denied, otp_expired, …) aren't user-facing
  // text; prefer its human-readable error_description and fall back to one
  // generic message rather than surfacing a raw code.
  return desc ? desc.replace(/\+/g, ' ') : 'The sign-in link is invalid or has expired.';
}

export const authRedirectError = captureAuthRedirectError();

export const supabase = url && anon ? createClient(url, anon) : null;
export const supabaseEnabled = !!supabase;
